import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { ApiError, NetworkError, api } from '../api';
import type { Journey, JourneyEntry, JourneyNote } from '../api-modules';
import { useI18n } from '../i18n';
import { EmptyState, ErrorState, Skeleton, Spinner } from '../components';
import { IconLock, IconUsers } from '../icons';
import { Dictate } from '../dictation/Dictation';

/**
 * The patient's journey in the clinic, visit by visit (spec: patient file,
 * 2026-10-21): on this day they came to this dentist, this was done, this is
 * what the dentist wrote, and this is planned for next time. A dentist opening
 * the file before an appointment reads the last visit and the plan first.
 *
 * Notes follow the clinic's privacy rule, enforced by the server: a dentist's
 * note is private (its author and the managers) unless they share it with the
 * clinic's dentists, who then read it without the author's name.
 */
export function VisitJourney({ patientId, canWriteNotes }: { patientId: string; canWriteNotes: boolean }) {
  const { t, formatDate, formatTime, locale } = useI18n();
  const [data, setData] = useState<Journey | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [writingFor, setWritingFor] = useState<string | null>(null);

  const load = useCallback(() => {
    setError(null);
    api
      .journey(patientId)
      .then(setData)
      .catch((e: unknown) => setError(e instanceof NetworkError ? t.errors.network : e instanceof ApiError ? e.message : t.common.error));
  }, [patientId, t]);
  useEffect(load, [load]);

  if (error) return <ErrorState message={error} onRetry={load} />;
  if (!data) return <div className="card"><Skeleton rows={6} /></div>;

  const name = (x: { nameEn: string; nameAr: string | null }) => (locale === 'ar' && x.nameAr ? x.nameAr : x.nameEn);
  const statusLabel = (s: string) => (t.journey.status as Record<string, string>)[s] ?? s;
  const attended = (e: JourneyEntry) => !['cancelled', 'no_show', 'scheduled', 'confirmed'].includes(e.status);

  return (
    <div className="journey">
      {data.upcoming.length > 0 || data.nextPlan || data.plan.length > 0 ? (
        <div className="journey-top">
          {data.upcoming.length > 0 ? (
            <div className="card journey-box">
              <div className="section-label">{t.journey.upcoming}</div>
              {data.upcoming.slice(0, 3).map((u) => (
                <div key={u.id} className="journey-line">
                  <strong>{formatDate(u.at)}</strong> · {formatTime(u.at)}
                  {u.dentist ? ` · ${t.journey.with.replace('{name}', u.dentist)}` : ''}
                  {u.reason ? <span className="muted"> · {u.reason}</span> : null}
                </div>
              ))}
            </div>
          ) : null}
          {data.nextPlan ? (
            <div className="card journey-box is-plan">
              <div className="section-label">{t.journey.nextPlan}</div>
              <p className="preserve-text" dir="auto" style={{ margin: 0 }}>{data.nextPlan}</p>
            </div>
          ) : null}
          {data.plan.length > 0 ? (
            <div className="card journey-box">
              <div className="section-label">{t.journey.plan}</div>
              <div className="chips">
                {data.plan.map((p) => (
                  <span key={p.id} className="chip">
                    {name(p)}
                    {p.tooth ? <span className="ltr-value"> · {p.tooth}</span> : null}
                  </span>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      ) : null}

      {data.entries.length === 0 ? (
        <EmptyState title={t.journey.none} />
      ) : (
        <ol className="journey-list">
          {data.entries.map((e) => (
            <li key={e.key} className={`journey-entry status-${e.status}`}>
              <div className="journey-when">
                <strong>{formatDate(e.at)}</strong>
                <span>{formatTime(e.at)}</span>
              </div>
              <div className="card journey-card">
                <div className="journey-head">
                  <span className={`badge journey-badge is-${e.status}`}>{statusLabel(e.status)}</span>
                  {e.dentist.name ? <span className="journey-dentist">{t.journey.with.replace('{name}', e.dentist.name)}</span> : null}
                  {e.reason ? <span className="muted">{e.reason}</span> : null}
                  {e.followup ? (
                    <span className="muted">{t.journey.followedUp.replace('{s}', (t.missed.status as Record<string, string>)[e.followup] ?? e.followup)}</span>
                  ) : null}
                </div>

                {e.done.length > 0 ? (
                  <div className="journey-done">
                    <div className="section-label">{t.journey.done}</div>
                    <ul>
                      {e.done.map((d) => (
                        <li key={d.id}>
                          {name(d)}
                          {d.tooth ? <span className="ltr-value"> · {d.tooth}</span> : null}
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : attended(e) && e.status !== 'note' ? (
                  <p className="muted" style={{ margin: '6px 0 0' }}>{t.journey.nothingDone}</p>
                ) : null}

                {e.notes.map((n) => (
                  <NoteView key={n.id} note={n} onChanged={load} />
                ))}

                {canWriteNotes && attended(e) && (e.appointmentId || e.visitIds.length > 0) ? (
                  writingFor === e.key ? (
                    <VisitNoteForm
                      patientId={patientId}
                      entry={e}
                      onCancel={() => setWritingFor(null)}
                      onSaved={() => {
                        setWritingFor(null);
                        load();
                      }}
                    />
                  ) : (
                    <button type="button" className="btn btn-sm btn-ghost journey-add" onClick={() => setWritingFor(e.key)}>
                      + {t.journey.addNote}
                    </button>
                  )
                ) : null}
              </div>
            </li>
          ))}
        </ol>
      )}
      {data.notesHidden ? <p className="muted">{t.journey.notesHidden}</p> : null}
    </div>
  );
}

function NoteView({ note, onChanged }: { note: JourneyNote; onChanged: () => void }) {
  const { t } = useI18n();
  const [busy, setBusy] = useState(false);
  const author = note.own ? t.journey.you : (note.author ?? t.journey.aDentist);
  const toggle = async () => {
    setBusy(true);
    try {
      await api.setNoteVisibility(note.id, note.visibility === 'private' ? 'shared' : 'private');
      onChanged();
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className={`journey-note is-${note.visibility}`}>
      <div className="journey-note-head">
        <span className={`note-vis is-${note.visibility}`} title={note.visibility === 'private' ? t.journey.privateHint : t.journey.shareHint}>
          {note.visibility === 'private' ? <IconLock size={13} /> : <IconUsers size={13} />}
          {note.visibility === 'private' ? t.journey.private : t.journey.shared}
        </span>
        <span className="muted">{author}</span>
        {note.own ? (
          <button type="button" className="btn btn-xs btn-ghost" onClick={toggle} disabled={busy}>
            {busy ? <Spinner /> : note.visibility === 'private' ? t.journey.makeShared : t.journey.makePrivate}
          </button>
        ) : null}
      </div>
      {/* Each note reads in its own direction: an English note inside the Arabic screen stays left-to-right. */}
      <p className="preserve-text" dir="auto">{note.body}</p>
      {note.nextPlan ? (
        <p className="journey-next">
          <strong>{t.journey.next}:</strong> <bdi>{note.nextPlan}</bdi>
        </p>
      ) : null}
    </div>
  );
}

function VisitNoteForm({
  patientId,
  entry,
  onCancel,
  onSaved,
}: {
  patientId: string;
  entry: JourneyEntry;
  onCancel: () => void;
  onSaved: () => void;
}) {
  const { t } = useI18n();
  const [body, setBody] = useState('');
  const [next, setNext] = useState('');
  const [share, setShare] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const add = (setter: (f: (v: string) => string) => void) => (text: string) => setter((v) => (v.trim() ? `${v.trimEnd()} ${text}` : text));

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!body.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await api.createNote({
        patientId,
        ...(entry.appointmentId ? { appointmentId: entry.appointmentId } : { visitId: entry.visitIds[0] }),
        body: body.trim(),
        nextPlan: next.trim() || null,
        visibility: share ? 'shared' : 'private',
        finalise: true,
      });
      onSaved();
    } catch (err) {
      setError(err instanceof NetworkError ? t.errors.network : err instanceof ApiError ? err.message : t.common.error);
      setBusy(false);
    }
  }

  return (
    <form className="journey-form stack" onSubmit={submit}>
      {error ? <div className="notice danger" role="alert">{error}</div> : null}
      <label className="field">
        <span className="field-label">
          {t.journey.whatDone}
          <Dictate onText={add(setBody)} disabled={busy} />
        </span>
        <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={4} placeholder={t.journey.whatDoneHint} dir="auto" autoFocus />
      </label>
      <label className="field">
        <span className="field-label">
          {t.journey.next} <span className="muted">({t.journey.nextHint})</span>
          <Dictate onText={add(setNext)} disabled={busy} />
        </span>
        <textarea value={next} onChange={(e) => setNext(e.target.value)} rows={2} dir="auto" />
      </label>
      <label className="check">
        <input type="checkbox" checked={share} onChange={(e) => setShare(e.target.checked)} />
        <span>
          {t.journey.share}
          <small className="muted" style={{ display: 'block' }}>{share ? t.journey.shareHint : t.journey.privateHint}</small>
        </span>
      </label>
      <div className="row" style={{ gap: 8, justifyContent: 'flex-end' }}>
        <button type="button" className="btn btn-ghost btn-sm" onClick={onCancel} disabled={busy}>{t.journey.cancel}</button>
        <button type="submit" className="btn btn-sm" disabled={busy || !body.trim()}>
          {busy ? <><Spinner /> {t.journey.saving}</> : t.journey.save}
        </button>
      </div>
    </form>
  );
}
